import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';

const ICONS = {
  overview: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none">
      <rect x="1" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="9" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="1" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="9" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  people: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M2 14c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  teams: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none">
      <circle cx="5" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="11" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M1 14c0-2.21 1.79-4 4-4M15 14c0-2.21-1.79-4-4-4M8 10c2.21 0 4 1.79 4 4M8 10c-2.21 0-4 1.79-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  schedule: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none">
      <rect x="1" y="2" width="14" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M5 1v2M11 1v2M1 6h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  meals: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none">
      <path d="M8 2v5M5 4c0 2.761 1.343 5 3 5s3-2.239 3-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M8 9v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  access: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M8 1v2M8 13v2M1 8h2M13 8h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  manual: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none">
      <rect x="3" y="1" width="10" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M6 5h4M6 8h4M6 11h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  settings: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M8 1v1.5M8 13.5V15M1 8h1.5M13.5 8H15M2.929 2.929l1.06 1.06M12.01 12.01l1.06 1.06M2.929 13.07l1.06-1.06M12.01 3.99l1.06-1.06" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  ),
  roster: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none">
      <rect x="1" y="3" width="14" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M4 7h4M4 10h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="12" cy="7" r="1" fill="currentColor"/>
    </svg>
  ),
  chevron: (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <path d="M2.5 3.5L5 6L7.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
};

const ADMIN_NAV = [
  { id: 'overview',      label: 'Overview',         icon: 'overview'  },
  { id: 'people',        label: 'People',           icon: 'people'    },
  { id: 'teams',         label: 'Teams',            icon: 'teams'     },
  { id: 'schedule',      label: 'Master Schedule',  icon: 'schedule'  },
  { id: 'meals',         label: 'Meal Counts',      icon: 'meals'     },
  { id: 'access',        label: 'Team Lead Access', icon: 'access'    },
  { id: 'admin-manual',  label: 'SL Manual',        icon: 'manual'    },
  { id: 'settings',      label: 'Settings',         icon: 'settings'  },
];

const PAST_NAV = [
  { id: 'past-2025', label: '2025', icon: 'schedule' },
];

const TL_SUB_ITEMS_BASE = [
  { sub: 'people',   label: 'People',         icon: 'people'   },
  { sub: 'roster',   label: 'Roster',         icon: 'roster'   },
  { sub: 'schedule', label: 'Team Schedule',  icon: 'schedule' },
  { sub: 'manual',   label: 'Team Documents',    icon: 'manual'   },
];

function getTlSubItems(teamId, teamRolesMap, team) {
  const rd = teamRolesMap?.[teamId];
  const rosterItems = (rd?.enabled && rd.roles.length > 0)
    ? rd.roles.map(role => ({ sub: `roster-${role}`, label: `${role} Roster`, icon: 'roster' }))
    : [{ sub: 'roster', label: 'Roster', icon: 'roster' }];

  // Kids staff view: the combined admin + team-lead nav (per the kids-team
  // meeting) — rosters + contributing + schedule, then the kid-data tabs.
  if ((team?.department || '').toString().trim().toLowerCase() === 'kids') {
    return [
      { sub: 'overview', label: 'Overview', icon: 'overview' },
      { sub: 'people', label: 'People', icon: 'people' },
      ...rosterItems,
      { sub: 'contributing', label: 'Contributing Teams', icon: 'teams' },
      { sub: 'schedule', label: 'Team Schedule', icon: 'schedule' },
      { divider: true },
      { sub: 'rotations', label: 'Rotations', icon: 'access' },
      { sub: 'daily', label: 'Daily Details', icon: 'schedule' },
      { sub: 'detail-sheets', label: 'Detail Sheets', icon: 'manual' },
      { sub: 'groups', label: 'Small Groups', icon: 'teams' },
      { sub: 'allergies', label: 'Allergies', icon: 'meals' },
      { sub: 'manual', label: 'Team Documents', icon: 'manual' },
      { sub: 'settings', label: 'Settings', icon: 'settings' },
    ];
  }

  if (rd?.enabled && rd.roles.length > 0) {
    return [
      { sub: 'people', label: 'People', icon: 'people' },
      ...rosterItems,
      { sub: 'schedule', label: 'Team Schedule', icon: 'schedule' },
      { sub: 'manual',   label: 'Team Documents',   icon: 'manual'   },
    ];
  }
  return TL_SUB_ITEMS_BASE;
}

export default function Sidebar({
  currentView, setCurrentView, isAdmin, showingAdminView,
  tlViewActive, selectedTeam, setSelectedTeam, teams, teamRolesMap,
  mobileOpen, onClose, scriptViewerUrl,
}) {
  const { user } = useAuth();

  // Collapsible state: Set of team IDs that are expanded
  const [expandedTeams, setExpandedTeams] = useState(new Set());

  // When teams list changes: expand first team only if 3+, expand all if 1-2
  const teamsKey = teams.map(t => t.id).join(',');
  useEffect(() => {
    if (teams.length >= 3) {
      setExpandedTeams(new Set(teams.slice(0, 1).map(t => t.id)));
    } else {
      setExpandedTeams(new Set(teams.map(t => t.id)));
    }
  }, [teamsKey]); // teamsKey is a primitive derived from teams — safe to use as dep

  function toggleTeam(teamId) {
    setExpandedTeams(prev => {
      const next = new Set(prev);
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }
      return next;
    });
  }

  function nav(viewId) {
    setCurrentView(viewId);
    if (onClose) onClose();
  }

  return (
    <nav className={`sidebar${mobileOpen ? ' mobile-open' : ''}`}>
      <div className="sidebar-logo">
        The Belonging Co
        <span>Conference 2026</span>
      </div>

      <div className="sidebar-nav">
        {showingAdminView && (
          <>
            {ADMIN_NAV.map(item => (
              <button
                key={item.id}
                className={`nav-item${currentView === item.id ? ' active' : ''}`}
                onClick={() => nav(item.id)}
              >
                {ICONS[item.icon]}
                {item.label}
              </button>
            ))}

            <div className="nav-section-label" style={{ marginTop: 8 }}>Past Years</div>
            {PAST_NAV.map(item => (
              <button
                key={item.id}
                className={`nav-item${currentView === item.id ? ' active' : ''}`}
                onClick={() => nav(item.id)}
              >
                {ICONS[item.icon]}
                {item.label}
              </button>
            ))}
          </>
        )}

        {!showingAdminView && teams.map((team, idx) => {
          const isExpanded = teams.length < 3 || expandedTeams.has(team.id);
          const isSelected = selectedTeam?.id === team.id;

          return (
            <div key={team.id} style={{ marginTop: idx > 0 ? 8 : 0 }}>
              {/* Team header — clickable to collapse/select */}
              <div
                className={`nav-sub-label${isSelected ? ' selected-team' : ''}`}
                style={{
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  paddingTop: 12, paddingBottom: isExpanded ? 4 : 10,
                  borderTop: idx > 0 ? '1px solid rgba(255,255,255,0.07)' : 'none',
                }}
                onClick={() => {
                  setSelectedTeam(team);
                  nav(`tl-${team.id}-people`);
                  if (teams.length >= 3) toggleTeam(team.id);
                }}
              >
                <span>{team.name}</span>
                {teams.length >= 3 && (
                  <span style={{
                    display: 'inline-flex',
                    opacity: 0.5,
                    transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 0.15s ease',
                  }}>
                    {ICONS.chevron}
                  </span>
                )}
              </div>

              {isExpanded && getTlSubItems(team.id, teamRolesMap, team).map((item, ii) => {
                if (item.divider) {
                  return <div key={`div-${ii}`} style={{ borderTop: '1px solid rgba(255,255,255,0.18)', margin: '8px 24px 6px' }} />;
                }
                const viewId = `tl-${team.id}-${item.sub}`;
                return (
                  <button
                    key={viewId}
                    className={`nav-sub-item${currentView === viewId ? ' active' : ''}`}
                    onClick={() => {
                      setSelectedTeam(team);
                      nav(viewId);
                    }}
                  >
                    {ICONS[item.icon]}
                    {item.label}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      {!showingAdminView && scriptViewerUrl && (
        <div style={{ padding: '8px 12px 4px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <a
            href={scriptViewerUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 10px', borderRadius: 'var(--radius)',
              fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700,
              color: 'rgba(255,255,255,0.55)', textDecoration: 'none',
              transition: 'color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = 'white'; e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; e.currentTarget.style.background = 'transparent'; }}
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0 }}>
              <rect x="1" y="3" width="8" height="9" rx="1" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M4 1h7a1 1 0 011 1v7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              <path d="M3.5 7l2 2L8.5 5.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Script Viewer
          </a>
        </div>
      )}

      <div className="sidebar-user">
        <div className="sidebar-user-name">{user?.name || 'Admin'}</div>
        <div className="sidebar-user-role">
          {tlViewActive ? 'Team Lead View' : user?.role === 'admin' ? 'Admin' : 'Team Lead'}
        </div>
      </div>
    </nav>
  );
}
